import {
  Component,
  effect,
  EventEmitter,
  Injectable,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { getState } from '@ngrx/signals';

@Component({
  selector: 'app-base-form',
  standalone: true,
  imports: [],
  templateUrl: './base-form.component.html',
  styleUrl: './base-form.component.scss',
})
@Injectable()
export abstract class BaseFormComponent<T extends { _id: string }>
  implements OnInit, OnDestroy
{
  abstract readonly store: any;
  form!: FormGroup;
  itemToEdit?: T;
  formInitialized = false;
  @Input() forceNew = false;
  @Input() cancelLevel = 1;
  @Input() disableCancelLink = false;
  @Output() formSubmitted: EventEmitter<T | undefined> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  protected constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected appUtilityService: AppUtilityService,
    protected routingService: RoutingService,
    protected permissionService: PermissionService,
    protected confirmationDialogService: ConfirmationDialogService
  ) {
    effect(
      () => {
        const state: any = getState(this.store);

        if (
          state.selectedId &&
          this.store.selectedItem() &&
          !state.error &&
          !this.formInitialized
        ) {
          this.itemToEdit = this.forceNew
            ? this.store.selectedItem()
            : undefined;
          this.initializeForm();
        }

        if (state.error) {
          this.confirmationDialogService.addErrorMessage(state.error.message);
        } else if (state.action && state.lastPersistedId) {
          this.routingService.navigateBack(this.cancelLevel);
          this.formSubmitted.emit(this.form?.value);
        }
      },
      { allowSignalWrites: true }
    );
  }

  // eslint-disable-next-line @angular-eslint/contextual-lifecycle
  ngOnInit() {
    this.hasAccess();
    this.cancelLevel = this.routingService.getCancelLevel() || this.cancelLevel;
    if (!this.formInitialized && this.store.selectedId()) {
      this.itemToEdit =
        this.store.selectedId() !== 'neu'
          ? this.store.selectedItem()
          : undefined;
      this.initializeForm();
    } else {
      this.itemToEdit = undefined;
      this.initializeForm();
    }
  }

  abstract initializeForm(): void;
  abstract onSubmit(): void;

  /**
   * Submits the form data. Updates or adds an item based on its state
   * and optionally navigates back to a previous route.
   *
   * @param {T} [overrideValue] - An optional value to override the form's current value.
   * @param {number} [navigateBack] - A flag specifying whether to navigate back after submission.
   *                                  If 0, no navigation occurs.
   * @return {void} This method does not return a value.
   */
  submit(overrideValue?: T, navigateBack?: number): void {
    if (this.form.valid) {
      if (this.itemToEdit && this.itemToEdit._id) {
        this.store.update({
          ...this.itemToEdit,
          ...(overrideValue || this.form.value),
        });
      } else {
        this.store.addItem(overrideValue || this.form.value);
      }
      //if (navigateBack === 0) return;
      //this.routingService.navigateBack(this.cancelLevel);
      //this.formSubmitted.emit(this.form?.value);
    } else {
      this.form.markAllAsTouched();
      this.appUtilityService.markAllAsDirty(this.form);
    }
  }

  /**
   * confirm deletion using p-confirm module
   * @param event
   * @param item
   * @param itemName
   */
  confirm(event: Event | MouseEvent | undefined, item: T, itemName: string) {
    this.confirmationDialogService.confirmDeletion(
      event,
      itemName,
      this.store.entityLabel(),
      () => this.remove(item)
    );
  }

  /**
   * removes item by form
   * deselect item in store
   * @param item
   */
  protected remove = (item: T) => {
    this.store.deleteItem(item);
    this.store.deselect();
  };

  ngOnDestroy() {
    this.formInitialized = false;
    this.store.deselect();
    this.itemToEdit = undefined;
    this.formSubmitted.emit(this.form?.value);
  }

  /**
   * Checks the access permissions for the current entity type and triggers appropriate permission checks based on the entity type.
   *
   * The method determines the entity type stored and performs access validation by calling functions from the permissionService.
   * The specific check logic and permissions applied depend on the `entityType` and the selected entity's details.
   *
   * @return {void} Does not return any value.
   */
  hasAccess(): void {
    const entityType = this.store.entityType();
    switch (entityType) {
      case 'Indicator':
        this.permissionService.checkIndicatorRights(
          this.store.selectedItem()?.category,
          ['edit', 'add'],
          true
        );
        break;
      case 'Category':
        this.permissionService.checkIndicatorRights(
          this.store.selectedItem()?._id,
          ['edit', 'add'],
          true
        );
        break;
      case 'BookmarkCategory':
        this.permissionService.checkBookmarkRights(
          this.store.selectedItem()?._id,
          ['edit', 'add'],
          true
        );
        break;
      case 'Bookmark':
        this.permissionService.checkBookmarkRights(
          this.store.selectedItem()?.category,
          'edit'
        );

        break;
      case 'Story':
        this.permissionService.checkBookmarkRights(
          this.store.selectedItem()?.category,
          ['edit', 'add'],
          true
        );
        break;
      case 'DataLayerType':
        this.permissionService.checkDataLayerRights(
          this.store.selectedItem()?._id,
          ['edit', 'add'],
          true
        );
        break;
      case 'DataLayer':
        this.permissionService.checkDataLayerRights(
          this.store.selectedItem()?.parent,
          ['edit', 'add'],
          true
        );
        break;
      case 'IndicatorData':
        this.permissionService.checkIndicatorRights(
          this.store.selectedItem()?._id,
          ['edit', 'add'],
          true
        );
        break;
      case 'Topic':
        this.permissionService.checkThemeRights(
          this.store.selectedItem()?._id,
          ['edit', 'add'],
          true
        );
        break;
    }
  }
}
